import { ReactElement } from 'react';
import {
    BallotCheckLightIcon,
    CalendarDayLightIcon,
    CoinsLightIcon,
    CommentsAltLightIcon,
    EnvelopeLightIcon,
    FileCertificateLightIcon,
    FileImportLightIcon,
    HandshakeAltLightIcon,
    LaptopCodeLightIcon,
    LaptopHouseLightIcon,
    TasksLightIcon,
    UserCircleLightIcon,
    UsersClassLightIcon,
    UsersLightIcon,
    WindowLightIcon,
} from '../Icons';

export type FeatureItem = {
    icon: ReactElement;
    label: string;
    grouping: 'booking_system' | 'online_course_lms' | 'training_crm';
    description: string;
    badge?: string;
};

export const listOfFeatures: Array<FeatureItem> = [
    // Booking System
    {
        label: 'In-person & Online Courses',
        grouping: 'booking_system',
        icon: <UsersClassLightIcon />,
        description:
            'Create courses, workshops and events that can be delivered in-person or online - via platforms like Zoom, Teams, Google Meet, etc. Simplify bookings, take payments, automate reminders and more.',
    },
    {
        label: 'Public & Private Bookings',
        grouping: 'booking_system',
        icon: <HandshakeAltLightIcon />,
        description:
            'Create public courses that are visible to everyone, or private courses and dates that are only visible to your team or select customers.',
    },
    {
        label: 'Calendar Sync & Scheduling',
        grouping: 'booking_system',
        icon: <CalendarDayLightIcon width={14} />,
        description:
            'Sync your Google or Outlook Calendar with Cademy and offer booking options based on your real-time availability.',
    },
    {
        label: 'Embeddable or Self-Hosted',
        grouping: 'booking_system',
        icon: <WindowLightIcon />,
        description:
            'Embed our booking system on your website or use our hosted site to sell courses and events.',
    },
    {
        label: 'Payments & Invoices',
        grouping: 'booking_system',
        icon: <CoinsLightIcon />,
        description:
            'Take card payments with Stripe, PayPal or Klarna - or offer your customer the option to request an invoice on checkout. Auto-issue invoices, track payemnts and send overdue invoice reminders on auto-pilot.',
    },
    // Online Course Builder & LMS
    {
        label: 'Online Course Builder & LMS',
        grouping: 'online_course_lms',
        icon: <LaptopCodeLightIcon />,
        description:
            'Create engaging online courses with videos, articles and other content in Cademy. Split courses into modules and lessons, and use quizzes and assessments to keep your students engaged.',
    },
    {
        label: 'Blended Courses',
        grouping: 'online_course_lms',
        icon: <LaptopHouseLightIcon />,
        description:
            'Combine online sessions, in-person workshops and on-demand content to be consumed by the student individually with our blended course feature.',
    },
    {
        label: 'SCORM Courses',
        grouping: 'online_course_lms',
        icon: <FileImportLightIcon />,
        description:
            'Create truly interactive courses with SCORM, and import your files directly into Cademy. Sell SCORM courses through Cademy or import your students in the Admin Dashboard, and then track their progress and completion.',
    },
    {
        label: 'Quizzes & Assessments',
        grouping: 'online_course_lms',
        icon: <BallotCheckLightIcon />,
        description:
            'Create quizzes and assessments directly within your courses, and mark submissions automatically or manually.',
    },
    {
        label: 'Communities',
        grouping: 'online_course_lms',
        icon: <CommentsAltLightIcon />,
        description:
            'Keep students engaged with a community hub, where they can discuss the course content, ask questions and share their progress.',
        badge: 'Coming Soon',
    },
    // Training CRM
    {
        label: 'Contact Management',
        grouping: 'training_crm',
        icon: <UsersLightIcon />,
        description:
            'Keep track of all your contacts in a single place. Easily search and filter to find the right people and bulk email them with pre-saved templates.',
    },
    {
        label: 'Emails & Reminders',
        grouping: 'training_crm',
        icon: <EnvelopeLightIcon />,
        description: 'Automate confirmation, reminder and follow-up emails to your customers.',
    },
    {
        label: 'Facilitator Management',
        grouping: 'training_crm',
        icon: <UserCircleLightIcon />,
        description:
            'Add your team and manage their availability, assigned courses and bookings. Control which courses and contacts are accessible with granular permissions and let Cadmey handle facilitator reminders and notifications.',
    },
    {
        label: 'Progress & Attendance Tracking',
        grouping: 'training_crm',
        icon: <TasksLightIcon />,
        description:
            'Keep track of your students attendance or progress in your self-paced courses.',
    },
    {
        label: 'Certificates',
        grouping: 'training_crm',
        icon: <FileCertificateLightIcon />,
        description:
            'Use our certificate builder to import and re-create your existing certificates in Cademy and auto-issue certificates to your students with zero effort.',
    },
];

export default listOfFeatures;
